/**
 * Lightweight toast notification system. Stacks at the bottom of the screen
 * (above the mobile toolbar) and auto-dismisses after 3 seconds.
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface Toast {
  id: number;
  message: string;
  tone: "info" | "success" | "error";
}

interface ToastCtx {
  push: (message: string, tone?: Toast["tone"]) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback<ToastCtx["push"]>((message, tone = "info") => {
    const id = ++counter;
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }, []);

  const ctx = useMemo(() => ({ push }), [push]);

  return (
    <Ctx.Provider value={ctx}>
      {children}
      {/* Persistent, always-mounted live region so screen readers reliably
       * announce toasts (a region created per-toast is often missed). */}
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="false"
        style={{
          position: "fixed",
          bottom: "calc(5rem + env(safe-area-inset-bottom))",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: "0.4rem",
          zIndex: 60,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              background:
                t.tone === "error" ? "#dc2626" : t.tone === "success" ? "#16a34a" : "#1f2937",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: 8,
              fontSize: "0.85rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              pointerEvents: "auto",
            }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("ToastProvider missing");
  return ctx;
}
